import { useQuery } from '@tanstack/react-query'
import { Link, useNavigate } from 'react-router-dom'
import { api } from '@/api/client'
import { PageHeader } from '@/app/shell'
import { Card, Skeleton } from '@/ui/primitives'
import { DataTable, type Coloana } from '@/ui/data-table'
import { NewMenu } from '@/ui/new-menu'
import { lei } from '@/lib/money'
import { type Etapa } from '@/lib/pipeline'

type Tablou = {
  urgent_today: { plecari: number; sosiri: number; scadente: number }
  financiar_week: Record<string, number>
  month_summary: Record<string, number | string>
  notifications_count: number
  eur_rate: number
}
type Rez = {
  id: number; number: string; partner: string | null; booking_customer: string | null
  status: string | null; begin_date: string | null; end_date: string | null
  destination: string | null; service: string | null; responsible: string | null
  total_value: number; currency: string | null
}
type ListaRez = { reservations: Rez[]; total: number }

/** Palnia comerciala, ca la TravelJoy: unde e fiecare dosar in relatia cu agentia.
 *  Etapele si numerele vin de la server (`/api/pipeline/etape`), nu din cod. */

const zi = (s: string | null) => (s ? new Date(s).toLocaleDateString('ro-RO', { day: '2-digit', month: 'short' }) : '—')

export function Dashboard() {
  const nav = useNavigate()
  const { data: eu } = useQuery({
    queryKey: ['eu'], queryFn: () => api.get<{ full_name: string | null; username: string }>('/users/me'),
    staleTime: 10 * 60_000, retry: false,
  })
  const { data, isPending } = useQuery({ queryKey: ['tablou'], queryFn: () => api.get<Tablou>('/dashboard/') })

  // „De rezolvat" citeste ACEEASI sursa ca ecranul Sarcini. Campul notifications_count din
  // /dashboard/ numara altceva (notificari nerezolvate din tabela) si arata 196 fata de 64.
  const { data: sarcini } = useQuery({
    queryKey: ['sarcini'],
    queryFn: () => api.get<{ summary: { total: number } }>('/dashboard/notifications'),
    staleTime: 60_000,
  })

  const pipeline = useQuery({
    queryKey: ['pipeline-etape'],
    queryFn: () => api.get<{ etape: Etapa[]; total: number }>('/pipeline/etape'),
    staleTime: 60_000,
  })
  const deFacut = useQuery({
    queryKey: ['de-facut'],
    queryFn: () => api.get<{ total: number }>('/pipeline/de_facut'),
    staleTime: 60_000,
  })

  const { data: apropiate, isPending: incarcaRez } = useQuery({
    queryKey: ['rez-apropiate'],
    queryFn: () => api.get<ListaRez>('/reservations/list-fast?limit=200&active_only=true'),
  })

  const randuri = (apropiate?.reservations ?? [])
    .filter((r) => r.begin_date)
    .sort((a, b) => (a.begin_date! < b.begin_date! ? -1 : 1))

  const coloane: Coloana<Rez>[] = [
    { cheie: 'number', antet: 'Rezervare', sortabil: true, valoare: (r) => r.number,
      randare: (r) => <span className="font-medium">#{r.number}</span> },
    { cheie: 'client', antet: 'Client', sortabil: true, valoare: (r) => r.booking_customer || r.partner || '',
      randare: (r) => <span className="truncate block max-w-[240px]">{r.booking_customer || r.partner || '—'}</span> },
    { cheie: 'destinatie', antet: 'Destinatie', sortabil: true, valoare: (r) => r.destination || '',
      randare: (r) => <span className="text-muted truncate block max-w-[200px]">{r.destination || '—'}</span> },
    { cheie: 'plecare', antet: 'Plecare', sortabil: true, valoare: (r) => r.begin_date || '',
      randare: (r) => <span className="tnum">{zi(r.begin_date)}</span> },
    { cheie: 'intoarcere', antet: 'Intoarcere', sortabil: true, valoare: (r) => r.end_date || '',
      randare: (r) => <span className="tnum text-muted">{zi(r.end_date)}</span> },
    { cheie: 'status', antet: 'Etapa', sortabil: true, valoare: (r) => r.status || '',
      randare: (r) => <span className="text-muted">{r.status || '—'}</span> },
    { cheie: 'agent', antet: 'Agent', sortabil: true, valoare: (r) => r.responsible || '',
      randare: (r) => <span className="text-muted">{r.responsible || '—'}</span> },
  ]

  const num = (v: unknown) => (typeof v === 'number' ? v : 0)
  const f = data?.financiar_week ?? {}
  const prenume = (eu?.full_name || eu?.username || '').split(' ')[0]

  return (
    <>
      <PageHeader title="Tablou" count={data ? `curs BNR+2%: ${data.eur_rate.toFixed(4)} lei/EUR` : undefined}
        actions={<NewMenu />} />

      <div className="px-8 py-6 space-y-7">
        <div>
          <h2 className="text-[22px] font-semibold tracking-tight">
            Bine ai venit{prenume ? `, ${prenume}` : ''}!
          </h2>
          <p className="mt-1 text-[14px] text-muted">
            {new Date().toLocaleDateString('ro-RO', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
          </p>
        </div>

        {/* Palnia: fiecare etapa duce in lista filtrata pe ea */}
        <div className="grid gap-2.5 grid-cols-2 md:grid-cols-4 xl:grid-cols-7">
          <button onClick={() => nav('/rezervari')}
            className="text-left bg-surface border border-line rounded-[var(--radius-card)] p-3.5 hover:border-accent transition-colors">
            <p className="text-[12px] text-muted">Toate</p>
            <p className="mt-1 tnum text-[22px] leading-none">
              {pipeline.data ? pipeline.data.total.toLocaleString('ro-RO') : '—'}
            </p>
          </button>
          {(pipeline.data?.etape ?? []).map((e) => (
            <button key={e.cod} onClick={() => nav(`/rezervari?etapa=${e.cod}`)}
              className="text-left bg-surface border border-line rounded-[var(--radius-card)] p-3.5 hover:border-accent transition-colors">
              <p className="text-[12px] text-muted">{e.nume}</p>
              <p className="mt-1 tnum text-[22px] leading-none">{(e.numar ?? 0).toLocaleString('ro-RO')}</p>
            </button>
          ))}
        </div>

        {/* Banii saptamanii, in doua ranuri scurte */}
        {isPending ? <Card><Skeleton rows={2} /></Card> : (
          <div className="grid gap-3 grid-cols-2 lg:grid-cols-4">
            <Cifra eticheta="De incasat (7 zile)" valoare={`${lei(num(f.de_incasat))} lei`} la="/bani/incasari" />
            <Cifra eticheta="De platit (7 zile)" valoare={`${lei(num(f.de_platit))} lei`} la="/bani/plati" />
            <Cifra eticheta="Plecari azi" valoare={String(data?.urgent_today?.plecari ?? 0)} la="/rezervari/plecari" />
            <Cifra eticheta="De rezolvat" valoare={String(sarcini?.summary.total ?? 0)} la="/sarcini" />
            <Cifra eticheta="Mingea la noi" valoare={String(deFacut.data?.total ?? 0)}
              la="/rezervari?asteptam=noi" />
          </div>
        )}

        <section className="space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="text-[15px] font-semibold">Calatorii in derulare</h3>
            <Link to="/rezervari" className="text-[13.5px] text-accent-ink hover:underline">Vezi toate</Link>
          </div>
          <DataTable
            randuri={randuri} coloane={coloane} cheie={(r) => r.id} incarca={incarcaRez}
            sortImplicit={{ cheie: 'plecare', sens: 'asc' }}
            cauta={(r, t) => `${r.number} ${r.partner ?? ''} ${r.booking_customer ?? ''} ${r.destination ?? ''}`.toLowerCase().includes(t)}
            placeholderCautare="Cauta rezervare, client, destinatie…"
            peRand={(r) => nav(`/rezervari/${r.id}`)}
            gol={{ titlu: 'Nicio calatorie activa', indiciu: 'Rezervarile cu intoarcerea in viitor apar aici.' }}
          />
        </section>
      </div>
    </>
  )
}

function Cifra({ eticheta, valoare, la }: { eticheta: string; valoare: string; la?: string }) {
  const corp = (
    <Card className="p-4 h-full transition-colors hover:border-line-strong">
      <p className="text-[12.5px] text-muted">{eticheta}</p>
      <p className="mt-1 tnum text-[20px] leading-none">{valoare}</p>
    </Card>
  )
  return la ? <Link to={la} className="block">{corp}</Link> : corp
}
